import React, { useContext, useState } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Car, Search, FileText, MessageSquare, Calendar, CheckCircle, Star,
  ArrowRight, Shield, Award, MapPin, Lock, TrendingUp, BadgeCheck, Fingerprint, PlayCircle
} from 'lucide-react';

const HERO_IMG = 'https://images.pexels.com/photos/15489246/pexels-photo-15489246.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const steps = [
  { n: 1, icon: FileText, title: 'Publica tu Necesidad', desc: 'Describe el servicio que necesitas: cambio de aceite, frenos, diagnóstico o cualquier reparación.', tag: '3 minutos' },
  { n: 2, icon: MapPin, title: 'Talleres Te Encuentran', desc: 'Los talleres certificados cercanos ven tu solicitud y preparan sus ofertas automáticamente.', tag: 'Radio 10–20 km' },
  { n: 3, icon: MessageSquare, title: 'Recibe Ofertas', desc: 'Compara precios, tiempos, ubicación y reputación. Tú decides cuál es la mejor opción.', tag: 'Ahorro hasta 30%' },
  { n: 4, icon: BadgeCheck, title: 'Elige el Mejor', desc: 'Selecciona la oferta que mejor se adapte. Revisa reviews, certificaciones y disponibilidad.', tag: 'Reviews verificados' },
  { n: 5, icon: Calendar, title: 'Agenda tu Cita', desc: 'Coordina fecha y hora en la plataforma. Recibe confirmación instantánea del taller.', tag: 'Flexible a tu horario' },
  { n: 6, icon: Shield, title: 'Servicio Garantizado', desc: 'El trabajo queda registrado en tu Car Passport con un registro inmutable y verificable.', tag: 'Registro inmutable' },
];

const govSynergies = [
  { title: 'Ministerio de Hacienda', desc: 'Integración futura con el sistema de pago de marchamos y verificaciones de cumplimiento vehicular.' },
  { title: 'MOPT (Revisión Técnica)', desc: 'Conexión con RTV: los talleres autorizados podrán registrar inspecciones directamente en tu Car Passport.' },
  { title: 'Registro Nacional', desc: 'Sincronización con el Registro de Propiedad. Transferencias con historial completo y verificable.' },
  { title: 'INS y Aseguradoras', desc: 'Acceso al historial para valoración de seguros. Vehículos bien mantenidos califican a primas reducidas.' },
];

const LandingPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  if (user) {
    window.location.href = user.user_type === 'client' ? '/client/dashboard' : '/taller/dashboard';
    return null;
  }

  const handleGetStarted = () => navigate('/auth');

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glass-header">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-900 font-heading">Flix</span>
            </div>
            <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
              <a href="#como-funciona" className="hover:text-purple-700 transition-colors" data-testid="nav-como-funciona">Cómo funciona</a>
              <a href="#car-passport" className="hover:text-purple-700 transition-colors" data-testid="nav-car-passport">Car Passport</a>
              <a href="#beneficios" className="hover:text-purple-700 transition-colors" data-testid="nav-beneficios">Beneficios</a>
              <button onClick={() => navigate('/demo')} className="flex items-center gap-1.5 hover:text-purple-700 transition-colors" data-testid="nav-demo">
                <PlayCircle className="w-4 h-4" /> Ver demo
              </button>
            </nav>
            <div className="flex items-center space-x-3">
              <Button variant="ghost" onClick={handleGetStarted} className="text-slate-700 hover:text-purple-700 hover:bg-purple-50" data-testid="header-login-btn">
                Iniciar Sesión
              </Button>
              <Button onClick={handleGetStarted} className="rounded-full bg-purple-600 hover:bg-purple-700 px-6 active:scale-95 transition-transform" data-testid="header-signup-btn">
                Registrarse
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero — split layout */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 animate-fade-up">
              <div className="inline-flex items-center gap-2 bg-purple-50 border border-purple-200 px-4 py-1.5 rounded-full mb-6">
                <Shield className="w-4 h-4 text-purple-700" />
                <span className="text-sm font-semibold text-purple-700">Plataforma verificada · Costa Rica</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-[1.05]">
                Los talleres vienen a <span className="text-purple-600">ti</span>
              </h1>

              <p className="text-lg text-slate-600 mb-8 max-w-xl leading-relaxed">
                Publica tu necesidad y recibe ofertas de talleres certificados. Compara precios, elige el mejor y mantén el historial de tu vehículo protegido.
              </p>

              {/* Search bar */}
              <div className="max-w-xl mb-4">
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-2xl p-2 shadow-sm focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-purple-400 transition-colors">
                  <Search className="w-5 h-5 text-slate-400 ml-3 shrink-0" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="¿Qué servicio necesitas? Ej: Cambio de aceite, frenos..."
                    className="border-0 focus-visible:ring-0 text-base shadow-none"
                    data-testid="search-input"
                  />
                  <Button
                    onClick={handleSearch}
                    className="rounded-xl bg-purple-600 hover:bg-purple-700 px-5 active:scale-95 transition-transform shrink-0"
                    data-testid="search-btn"
                  >
                    Buscar Talleres
                  </Button>
                </div>
                <p className="text-sm text-slate-500 mt-3">
                  <span className="font-semibold text-purple-700">Sin registro necesario</span> para buscar talleres
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button
                  onClick={handleGetStarted}
                  size="lg"
                  className="rounded-full bg-purple-600 hover:bg-purple-700 text-base px-8 py-6 active:scale-95 transition-transform"
                  data-testid="get-started-btn"
                >
                  Comenzar Gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={handleGetStarted}
                  variant="outline"
                  size="lg"
                  className="rounded-full text-base px-8 py-6 border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-purple-700 hover:border-purple-300 transition-colors"
                  data-testid="soy-taller-btn"
                >
                  Soy Taller
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-emerald-600" /> Talleres verificados</span>
                <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-emerald-600" /> Historial inmutable</span>
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-emerald-600" /> Reviews reales</span>
              </div>
            </div>

            {/* Hero visual */}
            <div className="lg:col-span-6 relative animate-fade-up delay-200">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-lg">
                <img src={HERO_IMG} alt="Taller moderno premium" className="w-full h-[420px] lg:h-[500px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent"></div>

                {/* Floating passport card */}
                <div className="absolute bottom-5 left-5 right-5 sm:right-auto sm:w-80 bg-white/90 backdrop-blur-xl border border-white/60 rounded-xl p-4 shadow-lg" data-testid="hero-passport-card">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Fingerprint className="w-4 h-4 text-purple-700" />
                      <span className="text-xs font-bold uppercase tracking-widest text-purple-700">Car Passport</span>
                    </div>
                    <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      <CheckCircle className="w-3 h-3" /> Verificado
                    </span>
                  </div>
                  <p className="font-mono-data text-sm text-slate-800">VIN: 3FA6P0H74FR•••••</p>
                  <p className="text-xs text-slate-500 mt-1">12 servicios registrados · Último: hace 15 días</p>
                </div>
              </div>

              {/* Floating rating badge */}
              <div className="hidden sm:flex absolute -top-4 -right-2 items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-md">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                <span className="text-sm font-bold text-slate-900">4.8</span>
                <span className="text-xs text-slate-500">promedio de talleres</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-6 py-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { value: '500+', label: 'Talleres certificados' },
              { value: '10,000+', label: 'Servicios registrados' },
              { value: '4.8/5', label: 'Calificación promedio' },
              { value: '+15%', label: 'Valor de reventa' },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-3xl font-bold text-slate-900 font-heading">{s.value}</p>
                <p className="text-sm text-slate-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section id="como-funciona" className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-16">
              <span className="text-sm font-bold uppercase tracking-widest text-purple-700">Proceso simple</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mt-3 mb-4">
                ¿Cómo funciona Flix?
              </h2>
              <p className="text-lg text-slate-600">
                Simple, rápido y transparente. Desde publicar tu necesidad hasta aumentar el valor de tu vehículo.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {steps.map((step) => (
                <div key={step.n} className="flix-card p-6 hover-lift" data-testid={`step-card-${step.n}`}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 bg-purple-50 border border-slate-200 rounded-xl flex items-center justify-center">
                      <step.icon className="w-5 h-5 text-purple-700" />
                    </div>
                    <span className="font-mono-data text-sm text-slate-300 font-semibold">0{step.n}</span>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{step.desc}</p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-700 bg-purple-50 border border-slate-200 px-3 py-1 rounded-full">
                    <CheckCircle className="w-3 h-3" /> {step.tag}
                  </span>
                </div>
              ))}
            </div>

            {/* ROI banner */}
            <div className="mt-12 rounded-2xl bg-slate-950 passport-dark-grid border border-slate-800 p-8 lg:p-12 text-white overflow-hidden relative" data-testid="roi-banner">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-400 mb-4">
                    <TrendingUp className="w-4 h-4" /> Momento de valor
                  </span>
                  <h3 className="text-2xl lg:text-3xl font-bold mb-4">Vende tu vehículo con historial certificado</h3>
                  <p className="text-slate-300 leading-relaxed mb-6">
                    Tu <strong className="text-white">Car Passport</strong> con historial completo aumenta significativamente el valor de tu vehículo. Los compradores pagan más por transparencia total.
                  </p>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Historial verificable de todos los servicios realizados</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Registro inmutable — imposible de falsificar</li>
                    <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> Certificaciones de talleres autorizados</li>
                  </ul>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: '+15%', label: 'Mayor precio' },
                    { value: '3x', label: 'Más confianza' },
                    { value: '60%', label: 'Venta más rápida' },
                  ].map((m) => (
                    <div key={m.label} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
                      <p className="text-2xl lg:text-3xl font-bold text-emerald-400 font-heading">{m.value}</p>
                      <p className="text-xs text-slate-400 mt-2">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Car Passport */}
      <section id="car-passport" className="py-24 bg-slate-950 passport-dark-grid text-white relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-14 items-center mb-16">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/5 border border-white/15 px-4 py-1.5 rounded-full mb-6">
                  <Fingerprint className="w-4 h-4 text-purple-400" />
                  <span className="text-sm font-medium text-purple-300">Identidad digital vehicular</span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">
                  Car Passport: tu historial vehicular certificado
                </h2>
                <p className="text-slate-300 leading-relaxed mb-8">
                  Car Passport es tu <strong className="text-white">identificación vehicular digital</strong>: un historial inmutable y verificable de cada servicio, reparación y transacción realizada a tu vehículo dentro de Flix.
                </p>
                <ul className="space-y-4">
                  {[
                    ['Mayor valor de reventa', 'Vehículos con historial documentado se venden hasta 15% más rápido y a mejor precio.'],
                    ['Trazabilidad total', 'Acceso inmediato a todo el historial de mantenimiento desde tu móvil.'],
                    ['Garantías respaldadas', 'Cada servicio queda registrado con fecha, taller y detalles específicos.'],
                    ['Prevención de fraude', 'Imposible alterar o falsificar el historial de tu vehículo.'],
                  ].map(([title, desc]) => (
                    <li key={title} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-300"><strong className="text-white">{title}:</strong> {desc}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Mock passport document */}
              <div className="relative">
                <div className="passport-grid rounded-2xl border border-slate-200 p-7 text-slate-900 shadow-2xl" data-testid="passport-mock-card">
                  <div className="flex items-center justify-between border-b border-slate-200 pb-4 mb-5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                        <Car className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-700">Car Passport</span>
                    </div>
                    <span className="font-mono-data text-[11px] text-slate-400">DOC Nº CP-2025-8841</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-5">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Vehículo</p>
                      <p className="text-sm font-bold">Toyota Corolla 2021</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Placa</p>
                      <p className="font-mono-data text-sm font-semibold">BQR-582</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">VIN</p>
                      <p className="font-mono-data text-sm">3FA6P0H74FR•••••</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Estado</p>
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                        <BadgeCheck className="w-3 h-3" /> Certificado
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2.5">
                    {[
                      ['Cambio de aceite y filtros', '12 May 2025', 'Taller Express'],
                      ['Frenos delanteros', '28 Feb 2025', 'Auto Service Pro'],
                      ['Revisión técnica RTV', '10 Ene 2025', 'Mecánica Central'],
                    ].map(([svc, date, taller]) => (
                      <div key={svc} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-lg px-3 py-2.5">
                        <div>
                          <p className="text-xs font-semibold text-slate-800">{svc}</p>
                          <p className="text-[11px] text-slate-500">{taller}</p>
                        </div>
                        <span className="font-mono-data text-[11px] text-slate-400">{date}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between mt-5 pt-4 border-t border-dashed border-slate-200">
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-500"><Lock className="w-3 h-3" /> Registro inmutable</span>
                    <span className="font-mono-data text-[11px] text-purple-700 font-semibold">sha256: a94f•••e2c1</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Gov synergies */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {govSynergies.map((g) => (
                <div key={g.title} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/[0.08] transition-colors">
                  <Award className="w-6 h-6 text-purple-400 mb-4" />
                  <h4 className="text-sm font-bold text-white mb-2">{g.title}</h4>
                  <p className="text-xs text-slate-400 leading-relaxed">{g.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-14">
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="rounded-full bg-white text-slate-900 hover:bg-slate-100 text-base px-10 py-6 active:scale-95 transition-transform"
                data-testid="passport-cta-btn"
              >
                Crear mi Car Passport Gratis
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section id="beneficios" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mb-16">
            <span className="text-sm font-bold uppercase tracking-widest text-purple-700">Beneficios</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mt-3">
              ¿Por qué elegir Flix?
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl">
            {[
              { icon: BadgeCheck, title: 'Talleres Verificados', desc: 'Todos los talleres pasan por un proceso de verificación y cuentan con reviews reales de clientes.' },
              { icon: TrendingUp, title: 'Mejor Precio', desc: 'Compara múltiples ofertas lado a lado y elige la que mejor se adapte a tu presupuesto.' },
              { icon: Shield, title: 'Car Passport', desc: 'Historial completo y seguro de todos los servicios realizados a tu vehículo. Transferible al vender.' },
            ].map((f) => (
              <div key={f.title} className="flix-card p-8 hover-lift">
                <div className="w-12 h-12 bg-purple-50 border border-slate-200 rounded-xl flex items-center justify-center mb-5">
                  <f.icon className="w-6 h-6 text-purple-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto flix-card p-12 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              ¿Listo para comenzar?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Únete a miles de usuarios que ya cuidan su vehículo con Flix
            </p>
            <Button
              onClick={handleGetStarted}
              size="lg"
              className="rounded-full bg-purple-600 hover:bg-purple-700 text-base px-10 py-6 active:scale-95 transition-transform"
              data-testid="cta-btn"
            >
              Comenzar Ahora — Es Gratis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Datos protegidos</span>
              <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Conexión segura SSL</span>
              <span className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5" /> Talleres certificados</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-12 border-t border-slate-800">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold font-heading">Flix</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Plataforma segura</span>
              <p>&copy; 2025 Flix. Todos los derechos reservados.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
